import cookie from 'cookie-cutter';
import { useEffect } from 'react';
import { RawIntlProvider } from 'react-intl';
import { connect } from 'react-redux';
import { Route, Switch } from 'react-router-dom';

import styles from './DynamicRegistrationWrapper.module.less';
import Consents from '../Pages/Consents';
import Forms from '../Pages/Forms';
import ChooseOrganization from '../Pages/Introduction/ChooseOrganization';
import Complete from '../Pages/Introduction/Complete';
import CovidLanding from '../Pages/Introduction/CovidLanding';
import Failure from '../Pages/Introduction/Failure';
import ForgotPassword from '../Pages/Introduction/ForgotPassword';
import HasAccount from '../Pages/Introduction/HasAccount';
import Login from '../Pages/Introduction/Login';
import Logout from '../Pages/Introduction/Logout';
import Signup from '../Pages/Introduction/Signup';
import Welcome from '../Pages/Introduction/Welcome';
import WelcomeIntl from '../Pages/Introduction/WelcomeIntl';
import { loadCookies } from '../Pages/Introduction/duck';
import Modals from '../Pages/Modals';
import KioskManager from '../Pages/Modals/KioskManager';
import Review from '../Pages/Review';

import NotYou from '$DReg/NotYou/NotYou';
import { Helmet, Spinner } from '$HVUI';
import { useIntlHelper } from '$Hooks';
import { setSentryTag } from '$Utils';

const companyCookie = cookie.get('company');
const shouldIntl =
    companyCookie?.includes('howard brown') ||
    companyCookie?.includes('ahf') ||
    companyCookie?.includes('chicanos por la causa') ||
    companyCookie?.includes('telehealth');
export const DynamicRegistration = props => {
    const { location, company, email, name, isKiosk } = props;
    const { ourIntl, updateIntl, loading } = useIntlHelper({
        shouldIntl: shouldIntl,
        appName: 'DynamicRegistration'
    });
    const isTelehealth = company?.title == 'Healthvana Telehealth';
    const companyName = company?.name;
    const ahfClass = companyName === 'ahf' ? styles['ahf'] : '';

    useEffect(() => {
        if (process.env.STORYBOOK_ENV) return;
        props.loadCookies();
    }, []);

    setSentryTag('kiosk-mode', isKiosk);

    // We only validate that company exists. There are mulitple
    // steps that the patient goes through, and multiple possible
    // invalid states. Use Sentry over time to identify problem areas.
    const validSession = company && company.title;

    const cancelBlacklist = [
        '/',
        '/logout',
        '/complete',
        '/covid-landing',
        '/choose-org'
    ];
    const showCancel =
        !cancelBlacklist.includes(location.pathname) && validSession;

    const content = () => {
        if (!validSession) {
            return (
                <Switch>
                    <Route key="logout" path="/logout" component={Logout} />
                    <Route
                        component={() => (
                            <Failure
                                header="Contact your clinic"
                                message="An error has occured. You reached this page without first going to the landing page of a valid Healthvana organization. Ask your clinic what URL you should use to register."
                            />
                        )}
                    />
                </Switch>
            );
        }
        return (
            <Switch>
                {shouldIntl ? (
                    <Route
                        exact
                        path="/"
                        render={() => (
                            <WelcomeIntl changeLanguage={updateIntl} />
                        )}
                    />
                ) : (
                    <Route exact path="/" component={Welcome} />
                )}
                <Route path="/covid-landing" component={CovidLanding} />
                <Route path="/choose-org" component={ChooseOrganization} />
                <Route path="/has-account" component={HasAccount} />
                <Route path="/login" component={Login} />
                <Route path="/signup" component={Signup} />
                <Route path="/forgot-password" component={ForgotPassword} />
                <Route path="/logout" component={Logout} />
                <Route path="/consents" component={Consents} />
                <Route path="/forms" component={Forms} />
                <Route
                    path="/review"
                    render={() => <Review ourIntl={ourIntl} {...props} />}
                />
                <Route path="/review-form/:definitionUUID" component={Forms} />
                <Route
                    path="/assessments"
                    render={() => <Forms assessments={true} />}
                />
                <Route
                    path="/complete"
                    component={() => (
                        <Complete
                            companyName={companyName}
                            isKiosk={isKiosk}
                            isTelehealth={isTelehealth}
                        />
                    )}
                />
                <Route
                    component={() => (
                        <Failure header={"Sorry, this page doesn't exist"} />
                    )}
                />
            </Switch>
        );
    };
    if (loading) {
        return (
            <Spinner
                id="lang-spinner"
                fullsize={true}
                color="#fff"
                style={{ width: '5em', margin: '3em' }}
            />
        );
    }
    return (
        <RawIntlProvider value={ourIntl}>
            <Helmet>
                <title>Registration</title>
                <meta
                    name="description"
                    content={'Register for your upcoming visit.'}
                />
            </Helmet>
            <div className={`${styles['container']} ${ahfClass}`}>
                <Modals
                    showLink={showCancel}
                    logoutPath="/logout"
                    isKiosk={isKiosk}
                />
                <main
                    className={`${styles['card']} ${
                        location.pathname === '/' ? styles['welcome-page'] : ''
                    }`}
                >
                    {/* Bonus div to align the spinner/other content easier */}
                    <div>{content()}</div>
                    {showCancel && <NotYou email={email} name={name} />}
                </main>
                <KioskManager isKiosk={isKiosk} shouldIntl={shouldIntl} />
            </div>
        </RawIntlProvider>
    );
};

export const mapStateToProps = (state, ownProps) => {
    return Object.assign(
        {},
        {
            company: state.introduction && state.introduction.company,
            email: state.introduction && state.introduction.email,
            name: state.introduction && state.introduction.name,
            isKiosk: state.introduction && state.introduction.isKiosk
        },
        ownProps
    );
};

export default connect(mapStateToProps, { loadCookies })(DynamicRegistration);
